import React from 'react'
import { useLocation } from 'react-router-dom'
import type { EmbedParams, EmbedTheme } from './types'
import './embedStyles.css'

/**
 * `?partner_ids=68,621` -> [68, 621].
 *
 * Both spellings are accepted on purpose. The app key-transforms JSON request
 * *bodies* between camelCase and snake_case, but never query strings, so a
 * hand-typed `?partnerIds=` would otherwise be dropped on the floor with no
 * error and no empty state -- the embed would just quietly show every partner.
 *
 * Anything that is not a positive integer is discarded rather than forwarded.
 * `Number('')` is 0 and `Number.isInteger(0)` is true, so a stray comma in
 * `?partner_ids=68,,621` would otherwise send a partner id of 0 to the API.
 */
function parsePartnerIds(params: URLSearchParams): number[] {
  const raw = params.get('partner_ids') ?? params.get('partnerIds')
  if (!raw) return []

  return raw
    .split(',')
    .map((value) => Number(value.trim()))
    .filter((id) => Number.isInteger(id) && id > 0)
}

export function useEmbedParams(): EmbedParams {
  const { search } = useLocation()
  const params = new URLSearchParams(search)

  const rawTheme = params.get('theme')
  const theme: EmbedTheme = rawTheme === 'dark' ? 'dark' : 'light'
  const rawMaxItems = params.get('maxItems')

  return {
    theme,
    accent: params.get('accent') || '#eab308',
    header: params.get('header'),
    maxItems: rawMaxItems ? Number(rawMaxItems) : null,
    partnerIds: parsePartnerIds(params),
  }
}

interface EmbedLayoutProps {
  children: React.ReactNode
}

/**
 * Cards render react-router <Link>s with app-relative paths, so a click inside
 * the iframe loads the entire app into the host page's 600px box. This used to
 * be a `<Helmet><base target="_blank" /></Helmet>`, which did nothing twice
 * over: react-helmet-async drops a <base> that has no href, and even a rendered
 * one would not have helped, because <Link> reads the anchor's own `target`
 * prop and calls preventDefault before the browser ever consults <base>.
 *
 * So intercept the click ourselves. Capture runs before React's bubble-phase
 * dispatch, so <Link> sees an already-prevented event and skips its pushState;
 * the destination then opens in the parent's browsing context instead.
 * Modifier-clicks and middle-clicks are left alone -- the browser's own
 * open-in-new-tab handling is already correct for those.
 */
function openInParentWindow(event: React.MouseEvent<HTMLDivElement>): void {
  if (event.defaultPrevented || event.button !== 0) return
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

  const anchor = (event.target as HTMLElement).closest?.('a[href]') as HTMLAnchorElement | null
  if (!anchor) return

  event.preventDefault()
  // anchor.href is the resolved absolute URL, which the new window needs.
  window.open(anchor.href, '_blank', 'noopener')
}

const EmbedLayout: React.FC<EmbedLayoutProps> = ({ children }) => {
  const { theme, accent, header } = useEmbedParams()

  return (
    <div
      className={`embed-root theme-${theme}`}
      onClickCapture={openInParentWindow}
    >
      {header && (
        <div className="embed-header" style={{ color: accent }}>
          {header}
        </div>
      )}
      <div className="embed-grid">
        {children}
      </div>
    </div>
  )
}

export default EmbedLayout
